import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Projects.css';
import { authFetch } from '../utils/apiHelper';
import { workspaceReady } from '../utils/workspace';
import { API_URL, UPLOADS_PUBLIC_URL } from '../config';
import { useAuth } from '../auth/AuthContext';
import ShareModal from './ShareModal';
import DownloadModal from './DownloadModal';
import { showPageLoading, hidePageLoading, waitForVisibleImages } from '../utils/pageLoading';
import RetryImage from './RetryImage';

// Hover previews need a real hover: touch screens (iPad, phones) never show
// them, so they don't download them either. ANY input, not the primary one:
// an iPad's primary input is always touch, so '(hover: hover)' was false
// even with a Magic Keyboard trackpad — no previews there. Live, so
// attaching / detaching the trackpad switches previews on / off.
const HOVER_QUERY = '(any-hover: hover) and (any-pointer: fine)';
function useCanHover() {
  const mq = React.useMemo(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(HOVER_QUERY) : null), []);
  const [canHover, setCanHover] = React.useState(() => !!mq?.matches);
  React.useEffect(() => {
    if (!mq) return undefined;
    const onChange = () => setCanHover(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mq]);
  return canHover;
}
import { showToast } from '../utils/toast';

const ShareIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 16V4" />
    <path d="M7 9l5-5 5 5" />
    <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4v12" />
    <path d="M7 11l5 5 5-5" />
    <path d="M5 20h14" />
  </svg>
);

// Hover-to-preview: if a model has a matching {modelId}.mov uploaded (not
// every model does — this is opt-in, uploaded directly to S3 outside the
// app's own upload flow), loop-play it over the thumbnail on hover.
// preload="auto" starts fetching every card's clip as soon as the grid
// renders (not lazily on hover) so playback starts instantly with no
// buffering delay the first time a card is hovered. A missing/failing file
// just fires onError, permanently falling back to the plain thumbnail for
// that card with no visible error and no retry.
//
// Playback itself is NOT gated behind hover (see below) — hover only ever
// toggles CSS opacity. Confirmed via a live isolated repro: Safari rejects
// an explicit play() call triggered from a mouseenter handler with
// NotAllowedError until the page has "sticky user activation" — i.e. a
// real click/tap has happened *somewhere* on the page first — even though
// the video is muted. Nothing on the actual Projects page guarantees that
// (hovering a card is normally the very first interaction), so hover-
// triggered play() would silently never work in Safari. Starting playback
// as soon as each clip is ready instead hits Safari's separate, gesture-
// free muted-autoplay allowance — once it's looping, hover just reveals it.
const ModelCard = ({ model, onOpen, onShare, onDownload, canDownload, previewsAllowed }) => {
  const canHover = useCanHover();
  const [hovering, setHovering] = React.useState(false);
  const [videoReady, setVideoReady] = React.useState(false);
  const [videoFailed, setVideoFailed] = React.useState(false);
  const videoRef = React.useRef(null);

  // Callback ref (not a plain useRef + effect) so `muted`/`defaultMuted` are
  // set on the DOM node the instant it exists — before Safari gets a chance
  // to evaluate its autoplay policy against it at all, rather than racing
  // React's own JSX-attribute application.
  const setVideoRef = (el) => {
    videoRef.current = el;
    if (el) {
      el.muted = true;
      el.defaultMuted = true;
    }
  };

  const handleLoadedData = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.play().then(() => {
        // Only now — actual confirmed playback, not just loaded data — treat
        // the clip as usable. Autoplay can be blocked by things entirely
        // outside this page's control (e.g. macOS Low Power Mode throttling
        // it), and if it is, we don't want hover to reveal a frozen first
        // frame that never plays — that reads as broken, not "no preview".
        // Falling back to setVideoFailed on rejection makes a blocked
        // autoplay behave identically, dynamically, to a model with no .mov
        // at all: the <video> unmounts and hover does nothing special.
        setVideoReady(true);
      }).catch(() => {
        setVideoFailed(true);
      });
    }
  };

  const handleMouseEnter = () => setHovering(true);
  const handleMouseLeave = () => setHovering(false);

  const showVideo = hovering && videoReady && !videoFailed;

  return (
    <div
      onClick={() => onOpen(model)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="projects-model-item"
    >
      <div className="projects-model-item-img">
        {/* Retries a failed load (1-16 s apart) instead of hiding the
            thumbnail for good after one failure. */}
        <RetryImage
          src={model.imageUrl}
          alt={model.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          quietFail
        />
        {/* Not until the page's thumbnails are in (previewsAllowed), and
            never on touch screens: every card's clip downloading at once
            (straight from S3, ~6 connections) held up the thumbnails. */}
        {canHover && previewsAllowed && !videoFailed && (
          <video
            ref={setVideoRef}
            src={`${UPLOADS_PUBLIC_URL}/${model.modelId}.mov`}
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={handleLoadedData}
            onError={() => setVideoFailed(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: showVideo ? 1 : 0,
              pointerEvents: 'none',
              transition: 'opacity 0.15s ease',
            }}
          />
        )}
        <div className="projects-model-item-scrim" />
        <p className="projects-model-item-title">{model.name}</p>
        {canDownload && (
          <button
            className="projects-model-download-btn"
            title="Download all assets"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(model);
            }}
          >
            <DownloadIcon />
          </button>
        )}
        <button
          className="projects-model-share-btn"
          onClick={(e) => {
            e.stopPropagation();
            onShare(model);
          }}
        >
          <ShareIcon />
        </button>
      </div>
    </div>
  );
};

const Projects = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canDownload = hasRole('sysAdmin');
  const [models, setModels] = React.useState([]);
  const [modelsLoading, setModelsLoading] = React.useState(true);
  // Hover previews start loading only once the loading screen is gone.
  const [previewsAllowed, setPreviewsAllowed] = React.useState(false);
  const [newProjectOpen, setNewProjectOpen] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState('');
  const [shareModalModel, setShareModalModel] = React.useState(null);
  const [downloadModalModel, setDownloadModalModel] = React.useState(null);

  const openShareModal = (model) => setShareModalModel(model);
  const closeShareModal = () => setShareModalModel(null);
  const openDownloadModal = (model) => setDownloadModalModel(model);
  const closeDownloadModal = () => setDownloadModalModel(null);

  // Centred in the gap between the header and the splat grid.
  const handleShareGenerated = () => showToast('Share link generated successfully', {
    position: 'top',
    between: [document.getElementById('top-nav'), document.querySelector('.projects-grid')],
  });

  useEffect(() => {
    if (shareModalModel || downloadModalModel) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [shareModalModel, downloadModalModel]);

  // Loading overlay (same as the scene's, spinner only) from mount until the
  // splat list is in AND the thumbnails visible on screen have loaded.
  const pageLoadingRef = React.useRef(false);
  const endPageLoading = React.useCallback(() => {
    if (!pageLoadingRef.current) return;
    pageLoadingRef.current = false;
    hidePageLoading();
  }, []);

  // The current workspace (utils/workspace.js) — the splats listed are its
  // splats, and each opens in it (its name shows in the settings menu).
  const [workspace, setWorkspace] = useState(null);
  useEffect(() => {
    workspaceReady().then(({ workspaceId, workspaces }) => {
      setWorkspace(workspaces.find((w) => w.workspaceId === workspaceId) || null);
    });
  }, []);

  useEffect(() => {
    pageLoadingRef.current = true;
    showPageLoading();
    authFetch(`${API_URL}?listModels=true`)
      .then(res => res.json())
      .then(data => setModels(data.models || []))
      .catch(err => console.error('Error loading models:', err))
      .finally(() => setModelsLoading(false));
    return endPageLoading; // navigating away mid-load must not leave it up
  }, [endPageLoading]);

  useEffect(() => {
    if (modelsLoading) return;
    // Two frames: let React commit the grid before looking for its images.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      waitForVisibleImages(document.querySelector('.projects-section')).then(() => {
        endPageLoading();
        setPreviewsAllowed(true);
      });
    }));
  }, [modelsLoading, endPageLoading]);

  // Load top HTML
  useEffect(() => {
    let cancelled = false;
    let fullscreenButton = null;
    let toggleFullscreen = null;
    let handleKeyDown = null;

    fetch('/html/dashboard-top.html')
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.text();
      })
      .then((html) => {
        if (cancelled) return;
        const container = document.getElementById('html-container');
        if (container) {
          container.innerHTML = html;
          container.querySelectorAll('script').forEach((oldScript) => {
            const newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            document.body.appendChild(newScript);
          });

          // Scene.js/captures.js wire this up on their own pages; this page
          // loads no such script, so the fullscreen button needs it here.
          fullscreenButton = document.getElementById('fullscreen-button');
          toggleFullscreen = () => {
            if (!document.fullscreenElement) {
              document.documentElement.requestFullscreen().catch((err) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
              });
            } else {
              document.exitFullscreen().catch((err) => {});
            }
          };
          if (fullscreenButton) {
            fullscreenButton.addEventListener('click', toggleFullscreen);
          }

          // Match the 'f' keyboard shortcut Scene/Captures support, so
          // fullscreen behaves consistently across pages. Removed on
          // unmount below — unlike scene.js/captures.js's own listeners,
          // which leak past navigation (see the login-page shortcut bug).
          handleKeyDown = (event) => {
            if (window.__accountModalOpen) return;
            const targetTag = event.target && event.target.tagName;
            if (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || (event.target && event.target.isContentEditable)) return;
            if (event.key.toLowerCase() === 'f') toggleFullscreen();
          };
          document.body.addEventListener('keydown', handleKeyDown);
        }
      })
      .catch((err) => console.error('Error loading top HTML:', err));

    return () => {
      cancelled = true;
      if (fullscreenButton && toggleFullscreen) fullscreenButton.removeEventListener('click', toggleFullscreen);
      if (handleKeyDown) document.body.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const sectionTitleStyle = {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'Inter, sans-serif',
    color: '#ffffff',
    letterSpacing: '0.01em',
    textTransform: 'none',
  };

  const renderGrid = (items) => <div className="projects-grid">{items}</div>;

  const projectItems = useMemo(
    () => [
      <button key="project-add" className="projects-add-button" onClick={() => setNewProjectOpen(true)}>+</button>,
    ],
    []
  );

  const modelItems = useMemo(
    () =>
      models.map((model) => (
        <ModelCard
          key={model.modelId}
          model={model}
          onOpen={(m) => navigate(`/scene?modelId=${m.modelId}${workspace ? `&ws=${encodeURIComponent(workspace.workspaceId)}` : ''}`)}
          onShare={openShareModal}
          onDownload={openDownloadModal}
          canDownload={canDownload}
          previewsAllowed={previewsAllowed}
        />
      )),
    // previewsAllowed too: without it the cards kept the first render's
    // `false` whenever the workspace was already known by the time the
    // thumbnails finished — no hover preview ever started.
    [models, navigate, canDownload, workspace, previewsAllowed]
  );

  return (
    <div className="page-container">
      {/* Top */}
      <div id="html-container" style={{ position: 'sticky', top: 0, zIndex: 100 }} />

      {/* Main content */}
      <div
        style={{
          width: '100%',
          flexGrow: 1,
          backgroundColor: '#20252b',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
          overflowY: 'auto',
        }}
      >
        {/* <section className="projects-section">
          <h2 style={sectionTitleStyle}>Projects</h2>
          <div className="projects-grid">
            <button className="projects-add-button" onClick={() => setNewProjectOpen(true)}>+</button>
            {newProjectOpen && (
              <div className="np-wrapper">
                <p className="np-title">New Project</p>
                <div className="np-card">
                  <input
                    className="np-input"
                    type="text"
                    placeholder="Project name"
                    value={newProjectName}
                    onChange={e => setNewProjectName(e.target.value)}
                    autoFocus
                  />
                  <button className="np-select-splat">Select Splat</button>
                  <div className="np-actions">
                    <button className="np-btn np-cancel" onClick={() => { setNewProjectOpen(false); setNewProjectName(''); }}>Cancel</button>
                    <button className="np-btn np-ok">OK</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section> */}

        <section className="projects-section">
          <h2 style={sectionTitleStyle}>Splats</h2>
          {!modelsLoading && (
            models.length === 0 ? (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif', margin: 0 }}>No content available.</p>
            ) : (
              renderGrid(modelItems)
            )
          )}
        </section>

        <footer
          style={{
            marginTop: 'auto',
            minHeight: 51,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12,
            color: 'rgba(255,255,255,0.3)',
            backgroundColor: '#262b32',
          }}
        >
          © {new Date().getFullYear()} Cinesplat
        </footer>
      </div>

      {shareModalModel && (
        <ShareModal model={shareModalModel} onClose={closeShareModal} onGenerated={handleShareGenerated} />
      )}

      {downloadModalModel && (
        <DownloadModal model={downloadModalModel} onClose={closeDownloadModal} />
      )}
    </div>
  );
};

export default Projects;
